import MuiAlert, { AlertProps as MuiAlertProps } from '@mui/material/Alert';
import MuiAlertTitle from '@mui/material/AlertTitle';
import * as React from 'react';

import { FlexBox } from '../box';
import { IconButton } from '../button';
import { IconContentCopy } from '../icon';
import { Link } from '../link';
import { styled } from '../styled';
import { getPaletteColor } from '../theme/color-utils';
import { Tooltip } from '../tooltip';
import { deepMerge, enumType, makeEnum, withVersion } from '../utils';

export type AlertProps = MuiAlertProps & {
    severity: AlertSeverity;
    iconAlign?:
        | 'baseline'
        | 'normal'
        | 'stretch'
        | 'center'
        | 'flex-start'
        | 'flex-end';
    errorId?: string;
    errorIdLabel?: string;
    copyTooltip?: string;
    copiedTooltip?: string;
    items?: { value: string; isLink?: boolean; href?: string }[];
};

export { MuiAlertTitle as AlertTitle };
export const AlertSeverity = makeEnum('error', 'info', 'success', 'warning');
export type AlertSeverity = enumType<typeof AlertSeverity>;

const StyledMuiAlert = styled(MuiAlert)<{ $severity: AlertSeverity }>(
    ({ theme, $severity }) => ({
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: getPaletteColor(
            theme,
            `components.alert.${$severity}.background`
        ),
        color: getPaletteColor(theme, `components.alert.${$severity}.color`),
        '& .MuiAlert-icon': {
            color: getPaletteColor(theme, `components.alert.${$severity}.color`)
        }
    })
);

const ErrorIdContainer = styled(FlexBox)<{ $severity: AlertSeverity }>(
    ({ theme, $severity }) => ({
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        color: getPaletteColor(theme, `components.alert.${$severity}.color`),
        fontSize: theme.typography.caption.fontSize,
        fontWeight: theme.typography.caption.fontWeight
    })
);

export const Alert = withVersion(
    React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
        const {
            errorId,
            errorIdLabel,
            copyTooltip,
            copiedTooltip,
            items,
            iconAlign,
            children,
            ...rest
        } = props;
        const [copied, setCopied] = React.useState(false);
        const slotProps = deepMerge({}, rest.slotProps ?? {}, {
            icon: {
                sx: {
                    alignItems: iconAlign ?? 'inherit'
                }
            }
        });

        const handleCopyErrorId = async () => {
            if (!errorId) {
                return;
            }
            await navigator.clipboard.writeText(errorId);
            setCopied(true);
            await new Promise((resolve) => setTimeout(resolve, 2000));
            setCopied(false);
        };

        return (
            <StyledMuiAlert
                {...rest}
                ref={ref}
                slotProps={slotProps}
                $severity={rest.severity}
            >
                {children}
                {items && items.length > 0 && (
                    <ul>
                        {items.map((item) => (
                            <li key={item.value}>
                                {item.isLink || item.href ? (
                                    <Link
                                        href={item.href ?? '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        color="inherit"
                                        underline="always"
                                        sx={{ textDecorationColor: 'inherit' }}
                                    >
                                        {item.value}
                                    </Link>
                                ) : (
                                    item.value
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {errorId && (
                    <ErrorIdContainer
                        data-role="alert-error-id"
                        $severity={rest.severity}
                    >
                        <Tooltip
                            {...(copied ? { open: true } : {})}
                            title={
                                copied
                                    ? (copiedTooltip ?? 'Copied!')
                                    : (copyTooltip ?? 'Copy code')
                            }
                        >
                            <IconButton
                                size="small"
                                color="inherit"
                                onClick={handleCopyErrorId}
                                aria-label={copyTooltip ?? 'Copy code'}
                            >
                                <IconContentCopy color="inherit" />
                            </IconButton>
                        </Tooltip>
                        {errorIdLabel ?? 'Error ID'}: {errorId}
                    </ErrorIdContainer>
                )}
            </StyledMuiAlert>
        );
    }),
    '1.1.0'
);
